import React, { useMemo, useState, useEffect } from 'react'

import actions from 'actions'
import { useDispatch, useSelector } from 'react-redux'
import { bindActionCreators } from 'redux'

import { getAvailableBoxSizes } from 'actions/basket'
import { boxSummaryDeliverySlotChosen } from 'actions/boxSummary'
import userActions from 'actions/user'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useMenuIsLoaded } from 'hooks/menu-metadata/useMenuIsLoaded'
import { useDoubleDeckerNav } from 'hooks/useDoubleDeckerNav'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketOrderId, getAvailableNumPortions } from 'selectors/basket'
import { getUserId, getUserOrdersWithRecipes, getUserStatus } from 'selectors/user'
import { getTempDeliveryOptions } from 'utils/deliverySlotHelper'

import { useBasket } from '../domains/basket'
import { useMenuPreferences } from '../domains/menu'
import { fetchData } from '../fetchData'
import { useIsSignupMotivations } from '../hooks/useIsSignupMotivations'
import { useRecipeGroupingsEnabled } from '../hooks/useRecipeGroupingsEnabled'
import {
  isMenuLoading,
  getMenuLoadingErrorMessage,
  getHasProspectNotSeenWizard,
} from '../selectors/menu'
import { shouldShowCapacityInfo } from '../selectors/signupReduction'
import { MenuRecipesPage } from './MenuRecipesPage'

type MenuRecipesPageWrapperProps = {
  params: {
    orderId?: string
  }
  location: {
    query: Record<string, unknown>
  }
}

export const MenuRecipesPageWrapper = (ownProps: MenuRecipesPageWrapperProps) => {
  const {
    params,
    location: { query = {} },
  } = ownProps
  const [hasUpdated, setHasUpdated] = useState(false)
  const dispatch = useDispatch()
  const menuIsLoaded = useMenuIsLoaded()
  const currentCollectionId = useCurrentCollectionId()
  const { numPortions } = useNumPortions()
  const { addRecipe } = useBasket()
  const isDoubleDeckerFeatureOn = useDoubleDeckerNav()
  const showLoading = useSelector(isMenuLoading)
  const storeOrderId = useSelector(getBasketOrderId)
  const userId = useSelector(getUserId)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const showCapacityInfo = useSelector(shouldShowCapacityInfo)
  const menuLoadingErrorMessage = useSelector(getMenuLoadingErrorMessage)
  const ordersWithRecipes = useSelector(getUserOrdersWithRecipes)
  const showMenuPreferencesTutorial = ordersWithRecipes.size > 1
  const isRecipeGroupingsEnabled = useRecipeGroupingsEnabled()
  const userStatus = useSelector(getUserStatus)

  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const { tempDate, tempSlotId } = useSelector(getTempDeliveryOptions)
  const availableBoxSize = useSelector(getAvailableNumPortions)
  const availableBoxSizeLength = availableBoxSize?.length

  const { data: menuPreferences, isLoading: menuPreferencesIsLoading } = useMenuPreferences()

  const isSignupMotivations = useIsSignupMotivations()

  useEffect(() => {
    if (hasProspectNotSeenWizard && tempSlotId && tempDate && !hasUpdated) {
      dispatch(boxSummaryDeliverySlotChosen({ slotId: tempSlotId, date: tempDate }))
      setHasUpdated(true)
    }
    if (!availableBoxSizeLength) {
      dispatch(getAvailableBoxSizes())
    }
    if (isAuthenticated && !userId) {
      dispatch(userActions.userLoadData())
    }
  }, [
    dispatch,
    hasProspectNotSeenWizard,
    tempDate,
    tempSlotId,
    availableBoxSizeLength,
    hasUpdated,
    userId,
    isAuthenticated,
  ])

  const actionDispatchers = useMemo(
    () =>
      bindActionCreators(
        {
          basketOrderLoaded: actions.basketOrderLoaded,
          portionSizeSelectedTracking: actions.portionSizeSelectedTracking,
          fetchMenuData: fetchData,
        },
        dispatch,
      ),
    [dispatch],
  )

  return (
    <MenuRecipesPage
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...ownProps}
      menuIsLoaded={menuIsLoaded}
      menuCurrentCollectionId={currentCollectionId}
      showLoading={showLoading || menuPreferencesIsLoading}
      orderId={params.orderId}
      storeOrderId={storeOrderId}
      numPortions={numPortions}
      userId={userId}
      shouldShowCapacityInfo={showCapacityInfo}
      menuLoadingErrorMessage={menuLoadingErrorMessage}
      query={query}
      addRecipe={addRecipe}
      // dispatches below
      basketOrderLoaded={actionDispatchers.basketOrderLoaded}
      portionSizeSelectedTracking={actionDispatchers.portionSizeSelectedTracking}
      fetchMenuData={actionDispatchers.fetchMenuData}
      isDoubleDeckerFeatureOn={isDoubleDeckerFeatureOn}
      menuPreferences={menuPreferences}
      showMenuPreferencesTutorial={showMenuPreferencesTutorial}
      isRecipeGroupingsEnabled={isRecipeGroupingsEnabled}
      isSignupMotivations={isSignupMotivations}
      userStatus={userStatus}
    />
  )
}
